import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  message,
  Checkbox,
  Select,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import IntlMessages from "util/IntlMessages";
import notify from "../Notification";
import {
  hideAuthLoader,
  hideMessage,
  showAuthLoader,
  userSignInSuccess,
  userSignInDataSuccess,
  setInitUrl,
} from "../appRedux/actions/Auth";
import CircularProgress from "../components/CircularProgress";
import AuthService from "../service/AuthService";
import { EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import getPostLoginRedirectUrl from "../util/AuthRedirect";

const { Option } = Select;

const SignIn = () => {
  const dispatch = useDispatch();
  const { loader, alertMessage, showMessage } = useSelector(({ auth }) => auth);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [locationStatus, setLocationStatus] = useState(""); // "" | "fetching" | "error"
  const [data, setData] = useState({
    uniqueId: "",
    password: "",
    userType: "",
  });
  const [coords, setCoords] = useState(null);

  const handelChange = (e) => {
    if (e.target) {
      const { name, value } = e.target;
      setData((preValue) => {
        return {
          ...preValue,
          [name]: value,
        };
      });
    } else {
      setData((preValue) => {
        return {
          ...preValue,
          ["userType"]: e,
        };
      });
    }
  };

  const onFinishFailed = (errorInfo) => {
    dispatch(hideAuthLoader());
    setIsLoading(false);
    console.log("Failed:", errorInfo);
  };
  const onChange = (e) => {
    if (checked === false) {
      setChecked(true);
      const stringifyData = JSON.stringify(data);
      localStorage.setItem("vendor-cred", stringifyData);
    } else {
      localStorage.removeItem("vendor-cred");
      setChecked(false);
    }
  };

  // ---- NEW: get browser geolocation as a Promise ----
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          // error.code: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 60000, //60 seconds wait for user to allow location access
          maximumAge: 60000,
        },
      );
    });
  };

  const onFinish = async (values) => {
    try {
      setIsLoading(true);
      let finalCoords = coords;
      if (!finalCoords) {
        setLocationStatus("fetching");
        try {
          finalCoords = await getUserLocation();
          setCoords(finalCoords);
        } catch (locError) {
          setIsLoading(false);
          dispatch(hideAuthLoader());

          let errMsg =
            "Location access is required to login. Please enable location and try again.";
          if (locError.code === 1) {
            errMsg =
              "Location permission denied. Please allow location access in your browser settings to login.";
          } else if (locError.code === 2) {
            errMsg =
              "Location unavailable. Please check your device's location/GPS settings.";
          } else if (locError.code === 3) {
            errMsg = "Location request timed out. Please try again.";
          }

          notify.openNotificationWithIcon("error", "Location Required", errMsg);
          return;
        }
      }
      dispatch(showAuthLoader());
      if (checked === true) {
        const stringifyData = JSON.stringify(data);
        localStorage.setItem("vendor-cred", stringifyData);
      }
      await AuthService.SignIn({
        userId: values.uniqueId?.toLowerCase(),
        password: values.password,
        userType: values?.userType,
        ...(values?.userType === "operator"
          ? {
              entityUserType: "vendor",
            }
          : {}),
        panel: "Vendor Panel",
        latitude: finalCoords.latitude,
        longitude: finalCoords.longitude,
      })
        .then((success) => {
          if (success.status === 200) {
            if (success.data?.isOtpRequired) {
              setIsLoading(false);
              dispatch(hideAuthLoader());
              dispatch(hideMessage());
              history.push("/verifyotp", {
                tempToken: success.data.tempToken,
                userId: success.data.userId,
                userType: success.data.userType,
                entityUserType: success.data.entityUserType,
                maskedEmail: success.data.maskedEmail,
              });
              return;
            }
            window.localStorage.setItem("user_token", success.data.token);
            window.localStorage.setItem("user", JSON.stringify(success.data));
            window.localStorage.setItem("hasModalBeenShown", true);
            notify.openNotificationWithIcon(
              "success",
              "Success",
              "Signin has been successfully done",
            );
            dispatch(userSignInSuccess(success));
            dispatch(userSignInDataSuccess(success.data));
            dispatch(hideMessage());
            dispatch(setInitUrl(""));
            const redirectUrl = getPostLoginRedirectUrl(success.data);

            if (redirectUrl) {
              history.replace("/" + redirectUrl);
            } else {
              history.replace("/erropage");
            }
          } else {
            setIsLoading(false);
            dispatch(hideMessage());
            if (success.status !== 422) {
              notify.openNotificationWithIcon(
                "error",
                "Error",
                success.message,
              );
            }
          }
        })
        .catch((err) => {
          dispatch(hideMessage());
          setIsLoading(false);
        });
    } catch (error) {
      dispatch(hideMessage());
      setIsLoading(false);
      notify.openNotificationWithIcon("error", "Error", error);
    }
  };

  useEffect(() => {
    const objectString = localStorage.getItem("vendor-cred");
    if (objectString) {
      const data2 = JSON.parse(objectString);
      setData(data2);
      setChecked(true);
    }
    // ---- NEW: fetch location as soon as page loads ----
    getUserLocation()
      .then((loc) => {
        setCoords(loc);
        setLocationStatus("ready");
      })
      .catch((err) => {
        setLocationStatus("error");
        console.log("Location error:", err);
      });
  }, []);

  return (
    <div
      className="gx-app-login-wrap"
      style={{
        backgroundColor: "rgb(4 74 143)",
        backgroundSize: "cover",
      }}
    >
      <div className="gx-app-login-container">
        <div className="gx-app-login-main-content">
          <div style={{ padding: "35px 35px 20px", width: "100%" }}>
            <Typography.Title level={1} style={{ textAlign: "center" }}>
              TES
            </Typography.Title>

            <Form
              fields={[
                {
                  name: ["uniqueId"],
                  value: data.uniqueId,
                },
                {
                  name: ["password"],
                  value: data.password,
                },
                {
                  name: ["userType"],
                  value: data.userType,
                },
              ]}
              initialValues={{ remember: true }}
              name="basic"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              className="gx-signin-form gx-form-row0"
            >
              <Form.Item name="userType" rules={[{ required: true }]}>
                <Select
                  placeholder="Select User Type"
                  onSelect={(e) => handelChange(e)}
                  onDeselect={(e) => handelChange(e)}
                  defaultValue={{ name: data.userType, value: data.userType }}
                  // disabled={title.includes("Edit Bank Account") ? true : false}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value={"operator"} key={"Operator"}>
                    Operator
                  </Option>
                  <Option value={"vendor"} key={"Vendor"}>
                    Vendor
                  </Option>
                </Select>
              </Form.Item>
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: "The input is not valid UserName!",
                  },
                ]}
                name="uniqueId"
              >
                <label style={{ fontSize: "20px" }}>User Name</label>
                <Input
                  placeholder="Vendor UserName"
                  name="uniqueId"
                  value={data?.uniqueId}
                  onChange={handelChange}
                />
              </Form.Item>
              <Form.Item
                rules={[
                  { required: true, message: "Please input your Password!" },
                ]}
                name="password"
              >
                <label style={{ fontSize: "20px" }}>Password</label>

                <Input.Password
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={data?.password}
                  onChange={handelChange}
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item>
                <div className="gx-form-row0">
                  <Checkbox onChange={onChange} checked={checked} />
                  <span> Remember Me</span>
                </div>
                {locationStatus === "fetching" && (
                  <div style={{ color: "#fff", marginBottom: "8px" }}>
                    Fetching your location, please allow permission...
                  </div>
                )}
                <Button
                  loading={isLoading}
                  block
                  type="primary"
                  className="gx-mb-0 gx-mt-2"
                  style={{
                    backgroundColor: "rgb(4 74 143)",
                    color: "white",
                    fontSize: "20px",
                    height: "50px",
                  }}
                  htmlType="submit"
                >
                  <IntlMessages id="app.userAuth.signIn" />
                </Button>
              </Form.Item>
            </Form>
          </div>

          {loader ? (
            <div className="gx-loader-view">
              <CircularProgress />
            </div>
          ) : null}
          {showMessage ? message.error(alertMessage.toString()) : null}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
